import { Routes, Route, Navigate } from "react-router-dom";
import { lazy } from "react";
import PrivateRoute from "../../components/Private/PrivateRoute";
import ChatWrapper from "../../components/Chat/ChatWrapper";
import ConversationsList from "../../components/Chat/ConversationsList";

import PaymentSuccess from "../../pages/PaymentSuccess";
import PaymentFailed from "../../pages/PaymentFailed";
import Payments from "../../pages/Payments";
import MyBooking from "../MyBooking";
import BookingOverview from "../BookingOverview";
import Packages from "../../components/package/Packages";
import ExploreProfessionals from "../../components/Home/ExploreProfessionals";
import MyFavourites from "../MyFavourites";
import BlogDetail from "src/components/Accessories/Blog/BlogDetail";
import HelpCenter from "../HelpCenter";
import { isCompletedProfessional } from "../../utils/authUtils";

/* ================= LAZY IMPORTS ================ */

// Public
const Home = lazy(() => import("../../pages/Home"));
const Help = lazy(() => import("../../components/Accessories/HelpCenter"));
const HelpCategory = lazy(
  () => import("../../components/Accessories/HelpCategory"),
);
const Blogs = lazy(() => import("../../components/Accessories/Blog/Blogs"));
const AboutUs = lazy(
  () => import("../../components/Accessories/AboutUs/AboutUs"),
);
const ContactUs = lazy(() => import("../../components/Accessories/ContactUs"));
const JoinUs = lazy(() => import("../../components/Accessories/JoinUs/JoinUs"));
const B2B = lazy(() => import("../../components/Accessories/B2B/B2B"));
const PrivacyPolicy = lazy(
  () => import("../../components/Accessories/PrivacyPolicy"),
);
const TermsAndConditions = lazy(
  () => import("../../components/Accessories/TermsAndConditions"),
);

const Faq = lazy(() => import("../../components/Home/components/FAQ/FAQ"));
const UnderConstruction = lazy(
  () => import("../../components/Accessories/UnderConstruction"),
);

const SeedBlog = lazy(() => import("../../pages/SeedBlog"));

// Auth
const ForgotPassword = lazy(
  () => import("../../components/auth/ForgotPassword"),
);
const ResetPassword = lazy(() => import("../../components/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("../../pages/VerifyEmail"));

// Profiles
const CustomerProfile = lazy(
  () => import("../../components/Profile/CustomerProfile"),
);
const Portfolio = lazy(() => import("../../components/Profile/Portfolio"));

// Event Listings
const EventManagersList = lazy(() => import("../../pages/EventManagersList"));
const EventManagerProfile = lazy(
  () => import("../../pages/EventManagerProfile"),
);
const VolunteersList = lazy(() => import("../../pages/VolunteersList"));

// Dashboard
const Dashboard = lazy(() => import("../../pages/Dashboard"));
const NotificationList = lazy(
  () => import("../../components/notifications/NotificationList"),
);

// Booking
const BookingSummary = lazy(() => import("../../pages/BookingSummary"));
const BookingSummaryPage = lazy(() => import("../../pages/BookingSummaryPage"));
const BookingCompletion = lazy(() => import("../../pages/BookingCompletion"));

// ✅ EVENT REQUIREMENT PAGES (OLD) - Removed

const EventDetailsPage = lazy(() => import("../../pages/EventDetailsPage"));

const CustomerPackageDetail = lazy(
  () => import("../../components/event/CustomerPackageDetail"),
);

/* ================= ROUTES ================= */

const NonProfessionalOnlyRoute = ({ element }) => {
  if (isCompletedProfessional()) {
    return <Navigate to="/dashboard/professional" replace />;
  }
  return element;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* ========== PUBLIC ROUTES ========== */}
      <Route path="/" element={<NonProfessionalOnlyRoute element={<Home />} />} />
      <Route path="/explore" element={<NonProfessionalOnlyRoute element={<ExploreProfessionals />} />} />
      {/* <Route path="/help" element={<Help />} />
      <Route path="/help/:slug" element={<HelpCategory />} /> */}
      <Route path="/blogs" element={<Blogs />} />
      <Route path="/blogs/:slug" element={<BlogDetail />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/join-us" element={<JoinUs />} />
      <Route path="/b2b" element={<B2B />} />
      <Route path="/seed-blog" element={<SeedBlog />} />

      <Route path="/organizers" element={<EventManagersList />} />
      <Route path="/profile/:id" element={<EventManagerProfile />} />
      <Route path="/volunteers" element={<VolunteersList />} />

      {/* ========== AUTH ROUTES ========== */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* ========== EVENT REQUIREMENT FLOW (NEW) ========== */}
      {/* Old requirement pages were removed. Wizard modal is used instead. */}

      {/* ========== PRIVATE ROUTES ========== */}
      <Route
        path="/dashboard/*"
        element={
          <PrivateRoute
            element={<Dashboard />}
            allowedRoles={["customer", "professional", "admin"]}
          />
        }
      />

      <Route
        path="/chat"
        element={
          <PrivateRoute
            element={<ConversationsList />}
            allowedRoles={["customer", "professional", "admin"]}
          />
        }
      />

      <Route
        path="/chat/:id"
        element={
          <PrivateRoute
            element={<ChatWrapper />}
            allowedRoles={["customer", "professional", "admin"]}
          />
        }
      />

      <Route
        path="/conversations"
        element={
          <PrivateRoute
            element={<ConversationsList />}
            allowedRoles={["customer", "professional", "admin"]}
          />
        }
      />

      <Route
        path="/portfolio"
        element={
          <PrivateRoute
            element={<Portfolio />}
            allowedRoles={["professional"]}
          />
        }
      />

      <Route
        path="/customer/profile"
        element={
          <PrivateRoute
            element={<CustomerProfile />}
            allowedRoles={["customer"]}
          />
        }
      />

      <Route
        path="/notifications"
        element={
          <PrivateRoute
            element={<NotificationList />}
            allowedRoles={["customer", "professional", "admin"]}
          />
        }
      />

      <Route
        path="/booking-summary/:eventId"
        element={
          <PrivateRoute
            element={<BookingSummary />}
            allowedRoles={["customer"]}
          />
        }
      />

      <Route
        path="/event/:eventId/booking-summary"
        element={
          <PrivateRoute
            element={<BookingSummaryPage />}
            allowedRoles={["customer"]}
          />
        }
      />

      <Route
        path="/booking-completion"
        element={
          <PrivateRoute
            element={<BookingCompletion />}
            allowedRoles={["customer"]}
          />
        }
      />

      <Route
        path="/my-booking"
        element={
          <PrivateRoute element={<MyBooking />} allowedRoles={["customer"]} />
        }
      />

      <Route path="/list/:customerId/:tabType" element={<MyFavourites />} />

      <Route
        path="/event/:eventId"
        element={<EventDetailsPage />}
      />
      <Route
        path="/events/:eventId"
        element={<EventDetailsPage />}
      />
      <Route
        path="/dashboard/customer/packages/:packageId"
        element={
          <PrivateRoute
            element={<CustomerPackageDetail />}
            allowedRoles={["customer"]}
          />
        }
      />

      <Route
        path="/packages"
        element={
          <PrivateRoute
            element={<Packages />}
            allowedRoles={["customer", "professional"]}
          />
        }
      />

      {/* ========== PAYMENTS ========== */}
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/failed" element={<PaymentFailed />} />
      <Route path="/all_orders" element={<Payments />} />
      <Route path="/event/:eventId/booking-overview" element={<BookingOverview />} />
      <Route path="/events/:eventId/booking-overview" element={<BookingOverview />} />

      {/* ========== HELP CENTER ========== */}
      <Route path="/help/*" element={<HelpCenter />} />

      {/* ========== LEGAL ========== */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

      {/* ========== FALLBACK ========== */}
      <Route path="*" element={<UnderConstruction />} />
    </Routes>
  );
};

export default AppRoutes;
